/** Provider/model prompt length helpers for Canvas Generate Image.
 *
 * Driven by `GET .../image-generation/config` provider_channels[].prompt_constraints
 * — never hardcode per-provider limits in node components. */

import type { TFunction } from 'i18next'
import type { ImageGenerationConfig, ImageGenerationProviderChannel } from '../../../api'
import { parseProviderChannels, resolveProviderChannel } from './imageGenerationProvider'

export const PROMPT_LIMIT_UNIT = 'characters'

export interface ImagePromptConstraints {
  maxCharacters: number | null
  unit: string
  source: string
}

export interface ImagePromptLimitState {
  length: number
  maxCharacters: number | null
  unit: string
  exceedsLimit: boolean
  nearLimit: boolean
  limitKnown: boolean
}

const DEFAULT_CONSTRAINTS: ImagePromptConstraints = {
  maxCharacters: null,
  unit: PROMPT_LIMIT_UNIT,
  source: 'unknown',
}

export function parsePromptConstraints(
  channel: ImageGenerationProviderChannel | null | undefined,
): ImagePromptConstraints {
  const raw = channel?.prompt_constraints
  if (!raw) return DEFAULT_CONSTRAINTS
  const max =
    typeof raw.max_characters === 'number' && Number.isFinite(raw.max_characters)
      ? raw.max_characters
      : null
  return {
    maxCharacters: max,
    unit: raw.unit || PROMPT_LIMIT_UNIT,
    source: raw.source || 'unknown',
  }
}

export function resolvePromptConstraintsForGenerateImageNode(args: {
  config: ImageGenerationConfig | null | undefined
  fields: Record<string, unknown>
}): ImagePromptConstraints {
  const channels = parseProviderChannels(args.config)
  const channel = resolveProviderChannel(args.fields, channels)
  return parsePromptConstraints(channel)
}

export function countPromptCharacters(text: string): number {
  return text.length
}

export function evaluateImagePromptLimit(
  prompt: string,
  constraints: ImagePromptConstraints,
): ImagePromptLimitState {
  const length = countPromptCharacters(prompt)
  const max = constraints.maxCharacters
  const limitKnown = max != null && max > 0
  const exceedsLimit = limitKnown && length > max
  const nearLimit = limitKnown && !exceedsLimit && length >= Math.max(1, Math.floor(max * 0.9))
  return {
    length,
    maxCharacters: max,
    unit: constraints.unit || PROMPT_LIMIT_UNIT,
    exceedsLimit,
    nearLimit,
    limitKnown,
  }
}

export function formatPromptLengthCounter(state: ImagePromptLimitState, locale?: string): string {
  const formattedLength = state.length.toLocaleString(locale)
  if (!state.limitKnown || state.maxCharacters == null) {
    return formattedLength
  }
  const formattedMax = state.maxCharacters.toLocaleString(locale)
  return `${formattedLength} / ${formattedMax}`
}

export function promptLimitValidationMessage(
  state: ImagePromptLimitState,
  t: TFunction<'canvas'>,
): string {
  if (!state.limitKnown || state.maxCharacters == null) return ''
  return t('generation.promptLimitValidation', {
    current: state.length.toLocaleString(),
    max: state.maxCharacters.toLocaleString(),
  })
}

export function localizeImageGenerationPromptLimitError(
  message: string | null | undefined,
  t: TFunction<'canvas'>,
): string | null {
  const normalized = (message || '').trim()
  if (!normalized) return null
  if (/prompt exceeds max length/i.test(normalized)) {
    return t('generation.promptLimitValidationGeneric')
  }
  return null
}
